import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, from, fromEvent, interval, of, throwError } from 'rxjs';
import { catchError, defaultIfEmpty, filter, first, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { ZendeskAuthRequest } from '@mp/shared/zendesk/domain';
import { ZendeskApiDetailsService, generateBase64Sha256, generateRandomString } from '@mp/shared/zendesk/util';

import { ZENDESK_OAUTH_SCOPES } from '../injection-tokens';

interface ZendeskCodeMessage {
  code: string;
  type: 'ZENDESK_OAUTH_CODE';
}

@Injectable()
export class ZendeskAuthService {
  private readonly zendeskApiDetailsService = inject(ZendeskApiDetailsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly http = inject(HttpClient);

  private get baseUrl(): string {
    return this.zendeskApiUrl || '';
  }

  private readonly backendApiBaseUrl = '/api/m/kernel/zendesk';

  private zendeskApiUrl: string | null = null;

  private readonly ZENDESK_ACCESS_TOKEN_MESSAGE_TYPE = 'ZENDESK_OAUTH_CODE';

  private readonly REDIRECT_URI: string = `${location.origin}/assets/zendesk/zendesk.html`;

  private readonly AUTH_SCOPE: string = inject(ZENDESK_OAUTH_SCOPES);

  private readonly CODE_VERIFIER_LENGTH = 64;

  constructor() {
    this.zendeskApiDetailsService.zendeskApiUrl$
      .pipe(
        tap((zendeskUrl) => (this.zendeskApiUrl = zendeskUrl)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  /**
   * Gets an access token for the Zendesk API. It runs the implicit grant flow in spawned popup window.
   * @param clientId - An OAuth client ID for Zendesk access.
   */
  getAuthToken(clientId: string): Observable<string | null> {
    if (!this.zendeskApiUrl) {
      return this.throwNoZendeskApiUrlError();
    }

    const codeVerifier = generateRandomString(this.CODE_VERIFIER_LENGTH);

    return from(generateBase64Sha256(codeVerifier)).pipe(
      map((codeChallenge) => ({ authWindow: this.openAuthFlowWindow(clientId, codeChallenge), codeVerifier })),
      switchMap(({ authWindow, codeVerifier }) => this.handleAuthWindow(authWindow, clientId, codeVerifier)),
    );
  }

  private openAuthFlowWindow(clientId: string, codeVerifier: string): Window | null {
    const endpoint = `${this.baseUrl}/oauth/authorizations/new`;

    const endpointParams: URLSearchParams = new URLSearchParams({
      response_type: 'code',
      redirect_uri: this.REDIRECT_URI,
      client_id: clientId,
      scope: this.AUTH_SCOPE,
      code_challenge_method: 'S256',
      code_challenge: codeVerifier,
    });

    return window.open(`${endpoint}?${endpointParams.toString()}`, undefined, 'width=800,height=600,popup');
  }

  private handleAuthWindow(window: Window | null, clientId: string, codeVerifier: string): Observable<string | null> {
    if (!window) {
      return of(null);
    }

    return this.listenForCodeMessage(window).pipe(
      filter((code) => code !== null),
      switchMap((code) => this.sendTokenExchangeRequest(code, clientId, codeVerifier)),
      defaultIfEmpty(null),
    );
  }

  private listenForCodeMessage(zendeskAuthWindow: Window): Observable<string | null> {
    const resultSubject$: Subject<string | null> = new Subject<string | null>();

    fromEvent<MessageEvent<ZendeskCodeMessage>>(window, 'message')
      .pipe(
        filter((message) => this.isZendeskCodeMessage(message, zendeskAuthWindow)),
        first(),
        map(({ data }) => data.code),
        takeUntil(this.getWindowClosedObservable(zendeskAuthWindow)),
        defaultIfEmpty(null),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((code) => resultSubject$.next(code));

    return resultSubject$.asObservable().pipe(first());
  }

  private isZendeskCodeMessage({ source, origin, data }: MessageEvent, zendeskAuthWindow: Window): boolean {
    return (
      source === zendeskAuthWindow &&
      origin === location.origin &&
      data?.type === this.ZENDESK_ACCESS_TOKEN_MESSAGE_TYPE &&
      typeof data.code === 'string'
    );
  }

  private getWindowClosedObservable(zendeskAuthWindow: Window): Observable<void> {
    const WINDOW_CLOSE_CHECK_INTERVAL = 200;

    return interval(WINDOW_CLOSE_CHECK_INTERVAL).pipe(
      map(() => zendeskAuthWindow.closed),
      filter(Boolean),
      map(() => undefined),
      first(),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  private sendTokenExchangeRequest(code: string, clientId: string, codeVerifier: string): Observable<string | null> {
    const requestBody: ZendeskAuthRequest = {
      grant_type: 'authorization_code',
      code: code,
      client_id: clientId,
      redirect_uri: this.REDIRECT_URI,
      scope: this.AUTH_SCOPE,
      code_verifier: codeVerifier,
    };

    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http
      .post<{ access_token: string }>(`${this.backendApiBaseUrl}/exchange-code`, requestBody, { headers })
      .pipe(
        map((response) => this.handleExtractAccessToken(response)),
        catchError((error: unknown) => this.handleError(error)),
      );
  }

  private handleExtractAccessToken(response: { access_token: string }): string | null {
    if (!response.access_token) {
      console.error('No access token in response');
      return null;
    }
    return response.access_token;
  }

  private handleError(error: unknown): Observable<never> {
    console.error(error);
    return throwError(() => new Error('Fehler beim Authentifizieren bei Zendesk'));
  }

  private throwNoZendeskApiUrlError(): Observable<never> {
    return throwError(() => new Error('Zendesk API URL not available'));
  }
}
